import React, { useState, useEffect, useRef } from 'react';
import {
  TextInput,
  Button,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Divider,
  PinInput,
  Anchor,
  Loader,
  Box
} from '@mantine/core';
import { IconMail, IconSend, IconAlertCircle } from '@tabler/icons-react';
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification
} from 'utils/notification';
import httpClient from 'services/httpClient';
import ReCAPTCHA from 'react-google-recaptcha';
import { BASE_URL } from 'services/api';
import { decodeShareToken } from 'utils/shareToken';

const isDevelopment = process.env.NODE_ENV === 'development';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENTERPRISE =
  (process.env.REACT_APP_RECAPTCHA_ENTERPRISE || 'false').toLowerCase() === 'true';

/* eslint-disable */
interface EmailFormProps {
  onSubmit: (email: string) => void;
  loading?: boolean;
  assessmentTitle?: string;
  assessmentDescription?: string;
  assessmentId?: string;
}
/* eslint-enable */

const EmailForm: React.FC<EmailFormProps> = ({
  onSubmit,
  loading: externalLoading = false,
  assessmentTitle = 'Assessment Schedule',
  assessmentDescription = 'Hi, we are excited to have you here! We hope you are ready to start the assessment process. Please follow every step carefully to ensure the confidentiality and integrity of our evaluation system.',
  assessmentId
}) => {
  /* eslint-disable */
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingHash, setValidatingHash] = useState(true);
  const [isHashValid, setIsHashValid] = useState<boolean | null>(null);
  const [step, setStep] = useState<'email' | 'verification'>('email');
  const [timeLeft, setTimeLeft] = useState(10800);
  const [expiresInMinutes, setExpiresInMinutes] = useState<number | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const maskKey = (k?: string | null) => (k ? `${k.substring(0, 6)}...` : 'undefined');

  const searchParams = new URLSearchParams(location.search);
  const encryptedId = searchParams.get('e') || '';
  const tokenParam = searchParams.get('tk') || '';
  const participantIdFromToken = (() => {
    if (!tokenParam) return undefined;
    const decoded = decodeShareToken(tokenParam);
    return decoded?.pid || undefined;
  })();

  // Dev flow removed
  const [devMode] = useState(false);
  const [mockCode] = useState<string>('');
  /* eslint-enable */

  useEffect(() => {
    const validateHash = async () => {
      if (!encryptedId) {
        setIsHashValid(false);
        setValidatingHash(false);
        return;
      }

      try {
        const response = await httpClient.get(
          `${BASE_URL}/assessment-schedules/external/validate-hash/${encryptedId}`,
          { headers: { 'X-Skip-Auth-Redirect': '1' } }
        );
        setIsHashValid(response.data.isValid);
      } catch {
        setIsHashValid(false);
      } finally {
        setValidatingHash(false);
      }
    };

    validateHash();
  }, [encryptedId, devMode]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    // Enterprise mode: load enterprise.js and alias grecaptcha to enterprise
    if (!RECAPTCHA_SITE_KEY || !RECAPTCHA_ENTERPRISE) {
      setRecaptchaReady(false);
      return;
    }

    const WIN: any = window as any;
    if (!WIN.__enterpriseRecaptchaReady) {
      WIN.__enterpriseRecaptchaReady = new Promise<void>((resolve, reject) => {
        const already = (window as any).grecaptcha?.enterprise;
        if (already && already.ready) {
          try {
            already.ready(() => {
              (window as any).grecaptcha = already; // alias to enterprise
              resolve();
            });
            return;
          } catch {
            // fallthrough to script load
          }
        }

        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/enterprise.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          const ent = (window as any).grecaptcha?.enterprise;
          if (ent && ent.ready) {
            ent.ready(() => {
              (window as any).grecaptcha = ent; // alias so react-google-recaptcha uses Enterprise API
              resolve();
            });
          } else {
            reject(new Error('reCAPTCHA Enterprise not available after load'));
          }
        };
        script.onerror = () => reject(new Error('Failed to load reCAPTCHA Enterprise'));
        document.head.appendChild(script);
      });
    }

    let cancelled = false;
    WIN.__enterpriseRecaptchaReady
      .then(() => {
        if (!cancelled) setRecaptchaReady(true);
      })
      .catch(() => {
        if (!cancelled) setRecaptchaReady(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    // Standard v2 mode: let the library inject api.js; just detect readiness
    if (!RECAPTCHA_SITE_KEY || RECAPTCHA_ENTERPRISE) return;
    let cancelled = false;
    const check = () => {
      const gre = (window as any).grecaptcha;
      if (gre && typeof gre.ready === 'function') {
        try {
          gre.ready(() => {
            if (!cancelled) setRecaptchaReady(true);
          });
        } catch {
          // ignore
        }
        return true;
      }
      return false;
    };
    if (!check()) {
      const iv = window.setInterval(() => {
        if (check()) window.clearInterval(iv);
      }, 150);
      return () => {
        cancelled = true;
        window.clearInterval(iv);
      };
    }
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.info(
        `[recaptcha] mode=${RECAPTCHA_ENTERPRISE ? 'enterprise' : 'v2-invisible'} key=${maskKey(
          RECAPTCHA_SITE_KEY
        )}`
      );
    }
  }, []);

  const handleSendVerification = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple email validation
    if (!email || !email.includes('@')) {
      showErrorNotification({
        message: 'Please enter a valid email address'
      });
      return;
    }

    setLoading(true);

    try {
      try {
        const response = await httpClient.post(
          '/assessment-schedules/external/send-verification',
          {
            email,
            assessmentId: encryptedId,
            ...(participantIdFromToken ? { trAssessmentParticipantID: participantIdFromToken } : {})
          },
          { headers: { 'X-Skip-Auth-Redirect': '1' } }
        );
        const { message, expiresInMinutes, expiresAt } = response.data || {};
        const alreadySent =
          typeof message === 'string' && message.toLowerCase().includes('already been sent');
        if (alreadySent) {
          const localExpiry = expiresAt
            ? new Date(expiresAt.endsWith('Z') ? expiresAt : expiresAt + 'Z').toLocaleString()
            : null;
          showWarningNotification({
            title: 'Code Already Sent',
            message: localExpiry
              ? `A verification code was already sent to your email. You can still use that code — it expires on ${localExpiry}.`
              : 'A verification code was already sent to your email. Please use that code to continue.'
          });
        } else {
          showSuccessNotification({ message: message || 'Verification code sent to your email' });
        }
        setExpiresInMinutes(expiresInMinutes || null);
        setExpiresAt(expiresAt || null);
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Failed to send verification code';
        showErrorNotification({ message: msg });
        setLoading(false);
        return;
      }

      setStep('verification');
      setTimeLeft(expiresInMinutes ? expiresInMinutes * 60 : 10800);

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      showErrorNotification({
        message:
          error instanceof Error
            ? error.message
            : 'An error occurred while sending verification code'
      });
    } finally {
      setCaptchaToken(null);
      setLoading(false);
    }
  };

  // Verify code and proceed
  const handleVerifySubmit = async () => {
    if (verificationCode.length !== 6) {
      showErrorNotification({
        message: 'Please enter the 6-digit verification code'
      });
      return;
    }

    setLoading(true);

    try {
      // Call the actual API and do not redirect to login on 401; just show error
      const response = await httpClient.post(
        '/assessment-schedules/external/verification',
        { email, verificationCode, assessmentId: encryptedId },
        { headers: { 'X-Skip-Auth-Redirect': '1' } }
      );

      const ok = response.status >= 200 && response.status < 300;
      const hasToken = Boolean(response.data?.token);
      const msg = String(response.data?.message || '').toLowerCase();
      const looksSuccessful = hasToken || msg.includes('success');
      if (!ok || !looksSuccessful) {
        throw new Error(response.data?.message || 'Invalid verification code');
      }

      showSuccessNotification({ message: 'Email verification successful' });
      onSubmit(email);
      if (timerRef.current) clearInterval(timerRef.current);
    } catch (error) {
      // If the server returned 401, treat it as an invalid verification code and show a helpful message
      const err: any = error;
      let message = 'An error occurred while verifying the code';
      if (err && err.response && err.response.status === 401) {
        message =
          'Invalid verification code. Please check the 6-digit code sent to your email and try again.';
      } else if (error instanceof Error) {
        message = error.message;
      }

      showErrorNotification({
        message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);

    try {
      const response = await httpClient.post(
        '/external/resend-verification',
        { email, assessmentId },
        { headers: { 'X-Skip-Auth-Redirect': '1' } }
      );

      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to send new verification code');
      }

      const { message, expiresInMinutes, expiresAt } = response.data || {};
      showSuccessNotification({ message: message || 'New verification code sent to your email' });
      setExpiresInMinutes(expiresInMinutes || null);
      setExpiresAt(expiresAt || null);
      setTimeLeft(expiresInMinutes ? expiresInMinutes * 60 : 10800);

      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      showErrorNotification({
        message:
          error instanceof Error
            ? error.message
            : 'An error occurred while sending new verification code'
      });
    } finally {
      setCaptchaToken(null);
      setLoading(false);
    }
  };

  // Format expiresAt to local string
  const formatLocalTime = (utcString: string | null) => {
    if (!utcString) return '';
    // Ensure UTC format by appending 'Z' if not present
    const utc = utcString.endsWith('Z') ? utcString : utcString + 'Z';
    const date = new Date(utc);
    return date.toLocaleString();
  };

  // Go back to email input
  const handleBackToEmail = () => {
    setStep('email');
    setVerificationCode('');

    // No explicit reset needed; widget resets after execute
    setCaptchaToken(null);

    // Clear timer
    if (timerRef.current) clearInterval(timerRef.current);
  };

  // Render based on hash validation status
  if (validatingHash) {
    return (
      <Paper p='xl' withBorder radius='lg'>
        <Stack align='center' gap='md'>
          <Loader size='md' color='#7B2183' />
          <Text>Validating assessment link...</Text>
        </Stack>
      </Paper>
    );
  }

  if (isHashValid === false) {
    return (
      <Paper p='xl' withBorder radius='lg'>
        <Stack align='center' gap='md'>
          <IconAlertCircle size={48} color='red' />
          <Title order={3}>Invalid Assessment Link</Title>
          <Text ta='center'>
            The assessment link you are trying to access is invalid or has expired. Please contact
            your administrator for assistance.
          </Text>
          <Button
            component='a'
            href='mailto:bcld.support@binus.edu'
            variant='outline'
            color='#7B2183'
          >
            Contact Support
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper p='xl' withBorder radius='lg'>
      <Stack gap='md'>
        <Title order={3}>{assessmentTitle}</Title>
        <Text color='dimmed' size='sm'>
          {assessmentDescription}
        </Text>

        <Divider my='sm' />

        {step === 'email' ? (
          <>
            <Text>
              Before you begin the assessment process, we need to verify your identity. This extra
              step ensures the confidentiality and integrity of our evaluation system.
            </Text>

            <form onSubmit={handleSendVerification}>
              <Stack gap='md'>
                <TextInput
                  label='Input Email'
                  placeholder='Input your email...'
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-autofocus
                />

                <Group justify='flex-end' mt='md'>
                  <Button
                    type='submit'
                    leftSection={<IconMail size={16} />}
                    color='#7B2183'
                    loading={loading || externalLoading}
                    fullWidth
                  >
                    Send Verification Code
                  </Button>
                </Group>
              </Stack>
            </form>
          </>
        ) : (
          <>
            <Title order={4}>Input Verification Code</Title>
            <Text>
              To continue, enter the 6 digit verification code sent to <strong>{email}</strong>
            </Text>

            <Group justify='center' mt='sm'>
              <PinInput
                length={6}
                size='md'
                value={verificationCode}
                onChange={setVerificationCode}
                oneTimeCode
              />
            </Group>

            <Group>
              <Text size='sm'>
                {expiresInMinutes && expiresAt ? (
                  <>
                    Verification code will{' '}
                    <Text component='span' c='red' inherit>
                      expire
                    </Text>{' '}
                    after <b>{expiresInMinutes}</b> minute{expiresInMinutes > 1 ? 's' : ''} (at{' '}
                    <b>{formatLocalTime(expiresAt)}</b>).
                  </>
                ) : (
                  <>Verification code expiration info not available.</>
                )}
                {timeLeft === 0 ? (
                  <Anchor
                    component='button'
                    ml={5}
                    onClick={handleResendCode}
                    underline='always'
                    color='#7B2183'
                  >
                    Send new code
                  </Anchor>
                ) : (
                  <Text component='span' inherit>
                    {' '}
                    Didn't receive the code? You can request a new code after expiration.
                  </Text>
                )}
              </Text>
            </Group>

            {/* reCAPTCHA is mounted once below */}

            <Text size='sm'>
              Having problem?{' '}
              <Anchor component='button' color='#7B2183' underline='always'>
                <a href='mailto:bcld.support@binus.edu'>Contact Us</a>
              </Anchor>
            </Text>

            <Group justify='flex-end' mt='md'>
              <Button variant='outline' color='#7B2183' onClick={handleBackToEmail}>
                Change Email
              </Button>
              <Button
                color='#7B2183'
                leftSection={<IconSend size={16} />}
                onClick={handleVerifySubmit}
                loading={loading || externalLoading}
              >
                Submit
              </Button>
            </Group>
          </>
        )}
        {/* Mount reCAPTCHA invisible widget via react-google-recaptcha only on the email step */}
        {step === 'email' && RECAPTCHA_SITE_KEY && (!RECAPTCHA_ENTERPRISE || recaptchaReady) && (
          <Box mt='md' aria-hidden>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              size='invisible'
              badge='inline'
              onErrored={() => {
                const hints = [
                  'Check the reCAPTCHA site key type (must be v2 Invisible, not v3 or Enterprise).',
                  'Add your domain to the allowed list in Google reCAPTCHA admin (e.g., localhost).',
                  'If you switched keys, restart the dev server so env vars reload.'
                ];
                showErrorNotification({
                  message: `reCAPTCHA error: Invalid site key. ${hints.join(' ')}`
                });
              }}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default EmailForm;
